import { useState } from "react";
import { Carousel } from "primereact/carousel";
import { Skeleton } from "primereact/skeleton";
import { TabPanel, TabView } from "primereact/tabview";
import { PathCard } from "sj-ui-toolkit/src/atlas/components/ContentCard/PathCard/PathCard";
import { C, themeClasses as T } from "sj-ui-toolkit/src/atlas/utils";
import type { Enrollment } from "sj-ui-toolkit/src/api/userEnrollments";

import carouselStyles from "../CarouselOverrides.module.scss";

interface MyPathsProps {
  currentPaths?: Enrollment[];
  currentQuests?: Enrollment[];
  isLoading?: boolean;
}

interface MyPathsContentProps {
  currentPaths: Enrollment[];
  currentQuests: Enrollment[];
}

const MyPathsContent = ({ currentPaths, currentQuests }: MyPathsContentProps) => {
  // The conditional TabPanels retain their source-child indexes: when only quests exist,
  // the quest panel is index 1. Initialize before the real TabView mounts to avoid a
  // post-load tab switch.
  const [activeIndex, setActiveIndex] = useState(currentPaths.length > 0 ? 0 : 1);

  return (
    <div className={carouselStyles.carouselContainer} data-testid="dashboard-my-paths">
      <TabView
        aria-label="My active enrollments"
        className={carouselStyles.carouselSection}
        activeIndex={activeIndex}
        onTabChange={e => setActiveIndex(e.index)}
        pt={{
          navContainer: { className: C("sticky top-0 z-2", T.bgElevation0) },
          nav: { className: C("atlas-h5", carouselStyles.sectionHeader) },
          panelContainer: { className: C("p-0", T.bgElevation0) },
        }}
      >
        {currentPaths.length > 0 && (
          <TabPanel header="My Paths">
            <Carousel
              aria-label="My Paths"
              className={C(
                carouselStyles.customCarousel,
                currentPaths.length <= 1 && carouselStyles.singlePageCarousel
              )}
              value={currentPaths}
              numVisible={1}
              numScroll={1}
              showIndicators={currentPaths.length > 1}
              showNavigators={currentPaths.length > 1}
              // No key needed: Carousel renders each template result inside its own keyed
              // CarouselItem wrapper — the template output is never a list child. (The old
              // key={index} was always undefined: PrimeReact calls itemTemplate(item) with
              // one arg.) `path` is contextually `any` from PrimeReact's signature, so the
              // Enrollment spread intentionally stays unchecked; PathCard normalizes at runtime.
              itemTemplate={path => (
                <PathCard {...path} cardSize="large" status="inProgress" isMyPaths />
              )}
            />
          </TabPanel>
        )}
        {currentQuests.length > 0 && (
          <TabPanel header="My Quests">
            <Carousel
              aria-label="My Quests"
              className={C(
                carouselStyles.customCarousel,
                currentQuests.length <= 1 && carouselStyles.singlePageCarousel
              )}
              value={currentQuests}
              numVisible={1}
              numScroll={1}
              showIndicators={currentQuests.length > 1}
              showNavigators={currentQuests.length > 1}
              itemTemplate={path => (
                <PathCard
                  {...path}
                  cardSize="large"
                  pathType="Quest"
                  status="inProgress"
                  isMyPaths
                />
              )}
            />
          </TabPanel>
        )}
      </TabView>
    </div>
  );
};

export const MyPaths = ({
  currentPaths = [],
  currentQuests = [],
  isLoading = false,
}: MyPathsProps) => {
  if (isLoading) {
    return (
      <div className={carouselStyles.carouselContainer}>
        <TabView
          aria-label="My active enrollments"
          className={carouselStyles.carouselSection}
          pt={{
            navContainer: { className: C("sticky top-0 z-2", T.bgElevation0) },
            nav: { className: C("atlas-h5", carouselStyles.sectionHeader) },
            panelContainer: { className: C("p-0 px-10", T.bgElevation0) },
          }}
        >
          <TabPanel header={<Skeleton width="150px" height="24px" />}>
            <Skeleton width="100%" height="400px" borderRadius="8px" />
          </TabPanel>
        </TabView>
      </div>
    );
  }

  if (!currentPaths.length && !currentQuests.length) {
    return null;
  }

  return <MyPathsContent currentPaths={currentPaths} currentQuests={currentQuests} />;
};
